<template>
  <section class="mt-4 space-y-3" aria-label="Propositions de remplacement">
    <p v-if="loadInfo" class="text-sm text-green-800" role="status">{{ loadInfo }}</p>
    <p v-if="loadError" class="text-sm text-red-700" role="alert">{{ loadError }} <button type="button" class="underline" @click="load">Réessayer</button></p>
    <article v-for="proposal in proposals" :key="proposal.id_jet" class="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <span role="status" class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ring-1 ring-inset" :class="replacementStatusClasses[displayStatus(proposal)]">{{ replacementStatus[displayStatus(proposal)] }}</span>
          <span class="text-xs text-gray-500">v{{ proposal.version }}</span>
        </div>
        <div v-if="proposal.statut === 'pending' && remainingSeconds(proposal) > 0" class="flex flex-wrap gap-3">
          <button v-if="canEditRecommendations && remainingSeconds(proposal) > 0" type="button" class="text-indigo-700 underline" @click="expandedProposalId = expandedProposalId === proposal.id_jet ? null : proposal.id_jet">Modifier</button>
          <button type="button" :disabled="busy" class="text-red-700 underline disabled:opacity-50" @click="cancel(proposal.id_jet)">Annuler</button>
        </div>
      </div>
      <template v-if="proposal.statut === 'pending' && remainingSeconds(proposal) > 0">
        <p class="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1.5 font-semibold tabular-nums text-amber-900 ring-1 ring-inset ring-amber-200" :title="`Réponse avant le ${date(responseDeadline(proposal))}`" role="timer" aria-label="Temps restant pour répondre">Temps restant : {{ countdown(proposal) }}</p>
        <p class="mt-2 text-gray-600">Sans réponse, les articles restent en rupture.</p>
      </template>
      <p v-if="hasExpiredLines(proposal)" class="mt-2 text-amber-800" role="status">Les articles sans réponse restent en rupture.</p>
      <details :open="expandedProposalId === proposal.id_jet" class="mt-2" @toggle="onDetailsToggle(proposal.id_jet, $event)"><summary class="cursor-pointer">Articles et montants</summary><ReplacementComparison :lines="proposal.lignes" :provisional="proposal.montants_provisoires" :editable="canEditRecommendations && proposal.statut === 'pending' && remainingSeconds(proposal) > 0" class="mt-3" @edit="(line, product, action) => openCandidateEditor(proposal, line, product, action)" /></details>
    </article>
    <EditReplacementCandidateModal v-if="canEditRecommendations" ref="candidateEditor" @saved="onCandidateSaved" />
    <ModalComp v-model:isVisible="visible" title="Proposer des remplacements" size="xl">
      <template #body>
        <ol ref="stepsHeader" tabindex="-1" class="mb-4 flex flex-wrap gap-3 text-sm" aria-label="Étapes de la proposition">
          <li :aria-current="step === 1 ? 'step' : undefined" :class="step === 1 ? 'font-semibold text-indigo-700' : 'text-gray-500'">1. Choisir les produits</li>
          <li :aria-current="step === 2 ? 'step' : undefined" :class="step === 2 ? 'font-semibold text-indigo-700' : 'text-gray-500'">2. Quantités et confirmation</li>
        </ol>
        <fieldset v-if="step === 1" class="mb-4 rounded-lg border p-3">
          <legend class="px-1 text-sm font-semibold">Articles en rupture ({{ selectedLineIds.length }})</legend>
          <div class="grid gap-2 sm:grid-cols-2">
            <label v-for="id in selectedLineIds" :key="id" class="flex items-start gap-2 rounded bg-red-50 p-2 text-sm">
              <input type="checkbox" :checked="originEnabled(id)" :disabled="busy" class="mt-1 shrink-0" @change="toggleOrigin(id)" />
              <span class="min-w-0"><strong class="break-words">{{ original(id)?.reference || id }}</strong><span class="block text-xs text-gray-600">{{ selectedFor(id).length }} candidat(s) proposé(s)</span></span>
            </label>
          </div>
        </fieldset>
        <label v-if="step === 1 && selectedLineIds.length > 1" class="mb-3 block text-sm">Article à remplacer
          <select v-model.number="searchTargetLineId" :disabled="busy" class="mt-1 block w-full rounded border p-2">
            <option v-for="id in selectedLineIds" :key="id" :value="id">{{ original(id)?.reference }} — ligne #{{ id }}</option>
          </select>
        </label>
        <form v-if="step === 1" class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 mb-4" @submit.prevent="searchProducts">
          <ReplacementReferenceSearch v-model="search" :products="referenceSuggestions" :selection="searchTarget" :loading="searching" :disabled="busy" @choose="chooseReference" @search="searchProducts" />
          <button type="submit" :disabled="searching || busy" class="col-start-2 row-start-1 border rounded px-3 py-2">{{ searching ? 'Recherche…' : 'Rechercher' }}</button>
        </form>
        <p v-if="step === 1 && appliedSearch" class="mb-4 text-xs text-gray-600">Recherche : « {{ appliedSearch }} »</p>
        <template v-if="step === 1">
        <div v-for="originId in selectedLineIds" :id="`replacement-origin-${originId}`" :key="originId" tabindex="-1" class="border rounded-lg p-3 mb-3 space-y-3">
          <div class="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-red-50 p-3">
            <div class="min-w-0 text-sm">
              <p class="font-semibold break-words">En rupture : {{ original(originId)?.reference || originId }}</p>
              <p class="mt-1 text-gray-600">{{ original(originId)?.categorie || 'Catégorie non renseignée' }}<template v-if="originalQuantity(originId)"> · {{ originalQuantity(originId) }} pièce(s)</template></p>
            </div>
            <div v-if="originalAmount(originId)" class="text-sm">
              <p class="font-semibold">Origine : {{ originalAmount(originId) }}</p>
              <p class="text-xs text-gray-600">Avant remises</p>
            </div>
            <p v-else class="text-xs text-gray-600">Montant d’origine non renseigné.</p>
          </div>
          <ReplacementProductPicker
            v-if="originEnabled(originId)"
            :products="availableProducts(originId)"
            :selected-ids="selectedFor(originId).map(s => s.id_produit_remplacement)"
            :selections="selectedFor(originId)"
            :loading="searching"
            :disabled="busy"
            @choose="toggleProduct(originId, $event)"
          />
          <div v-for="selection in selectedFor(originId)" :key="selection.id_produit_remplacement" class="space-y-3 rounded-lg border p-3">
            <button type="button" :disabled="busy" class="float-right text-sm text-red-700 underline" @click="removeProduct(selection)">Retirer</button>
            <p class="rounded bg-indigo-50 p-2 text-sm text-indigo-800"><strong>{{ product(selection)?.reference }}</strong> · {{ product(selection)?.couleur }}</p>
          </div>
        </div>
        </template>
        <div v-else class="space-y-4" aria-label="Quantités et montants des remplacements">
          <section v-for="originId in selectedLineIds.filter(originEnabled)" :id="`replacement-origin-${originId}`" :key="originId" tabindex="-1" :aria-label="`Remplacements pour ${original(originId)?.reference || originId}`">
            <article class="overflow-hidden rounded-lg border border-gray-200">
              <div class="grid sm:grid-cols-2">
                <div class="min-w-0 space-y-3 bg-red-50 p-4 text-sm">
                  <p class="text-xs font-semibold uppercase text-gray-600">En rupture</p>
                  <div class="flex items-start gap-3">
                    <img v-if="original(originId)?.imageUrl" :src="original(originId)?.imageUrl || ''" alt="" class="h-24 w-20 shrink-0 rounded bg-white object-contain" referrerpolicy="no-referrer" />
                    <div class="min-w-0 space-y-2 break-words">
                      <p class="font-semibold">{{ original(originId)?.reference || originId }}</p>
                      <p>{{ original(originId)?.color }}</p>
                      <p>{{ originalSizes(originId) }}</p>
                      <p><template v-if="original(originId)?.vendu_par !== 'tailles' && original(originId)?.quantite_pack">{{ original(originId)?.quantite_pack }} paquet(s) · </template>{{ originalQuantity(originId) }} pièce(s)</p>
                      <p v-if="quotedLine(originId)">{{ replacementMoney(quotedLine(originId)?.origine.montant_ht ?? 0) }} HT · {{ replacementMoney(quotedLine(originId)?.origine.montant_ttc ?? 0) }} TTC</p>
                      <p v-else>{{ originalAmount(originId) || 'Montant d’origine non renseigné.' }}</p>
                    </div>
                  </div>
                </div>
                <div class="min-w-0 space-y-4 bg-green-50 p-4 text-sm">
                  <p class="text-xs font-semibold uppercase text-gray-600">Remplacements proposés<template v-if="selectedFor(originId).length > 1"> · 1 au choix</template></p>
                  <div v-for="selection in selectedFor(originId)" :key="selection.id_produit_remplacement" class="space-y-3 border-b border-green-100 pb-4 last:border-0 last:pb-0">
                    <div class="flex items-start gap-3">
                      <img v-if="product(selection)?.image_url" :src="product(selection)?.image_url" alt="" class="h-24 w-20 shrink-0 rounded bg-white object-contain" referrerpolicy="no-referrer" />
                      <div class="min-w-0 flex-1 space-y-2 break-words">
                        <p class="font-semibold">{{ product(selection)?.reference }}</p>
                        <p>{{ product(selection)?.couleur }}</p>
                      </div>
                      <button type="button" :disabled="busy" class="shrink-0 text-red-700 underline disabled:opacity-50" @click="removeProduct(selection)">Retirer</button>
                    </div>
                    <label v-if="product(selection)?.vendu_par !== 'tailles'" class="block">Quantité de paquets
                      <input v-model.number="selection.quantite_pack" :disabled="busy" type="number" min="1" max="10000" class="mt-1 block w-28 rounded border p-2" @input="updatePacks(selection)" />
                    </label>
                    <p v-if="product(selection)?.vendu_par !== 'tailles'" class="text-xs text-gray-600">0 = taille non souhaitée. Modifier les paquets réinitialise les quantités.</p>
                    <div class="flex flex-wrap gap-3">
                      <label v-for="size in sizeInputs(selection)" :key="size.index">{{ size.label }}
                        <input :value="selection.quantites[size.index]" :disabled="busy" type="number" min="0" :max="product(selection)?.vendu_par === 'tailles' ? 10000 : Math.min(10000, (product(selection)?.pack[size.index] || 0) * selection.quantite_pack)" class="mt-1 block w-20 rounded border p-2" @input="updateSizeQuantity(selection, size.index, $event)" />
                      </label>
                    </div>
                    <p>{{ selection.quantites.reduce((sum, q) => sum + (Number(q) || 0), 0) }} pièce(s)</p>
                    <template v-if="quotedProduct(selection)">
                      <p>{{ replacementMoney(quotedProduct(selection)?.montant_ht ?? 0) }} HT · {{ replacementMoney(quotedProduct(selection)?.montant_ttc ?? 0) }} TTC</p>
                      <p v-if="quotedProduct(selection)?.charge_vendeur_ttc" class="text-amber-800">Charge vendeur si choisi : {{ replacementMoney(quotedProduct(selection)?.charge_vendeur_ttc ?? 0) }} TTC</p>
                      <p v-else-if="quotedProduct(selection)?.a_rembourser_ttc" class="text-blue-700">Remboursement estimé : {{ replacementMoney(quotedProduct(selection)?.a_rembourser_ttc ?? 0) }} TTC (hors remise fixe)</p>
                    </template>
                    <ul v-else-if="!selectionReady(selection)" class="space-y-1 text-amber-800" role="status">
                      <li v-for="message in quantityErrors(selection)" :key="message">{{ message }}</li>
                    </ul>
                    <p v-else class="text-indigo-700" role="status">{{ quoting ? 'Calcul du devis…' : 'Devis indisponible.' }}</p>
                  </div>
                  <template v-if="!selectedFor(originId).length">
                    <p>Aucun candidat proposé.</p>
                    <button type="button" :disabled="busy" class="font-medium text-indigo-700 underline" @click="focusOrigin(originId)">Choisir un produit</button>
                  </template>
                </div>
              </div>
            </article>
          </section>
        </div>
        <p v-if="step === 2 && preview && fixedDiscount" class="mt-3 text-sm text-gray-600">Remise prise en compte : {{ replacementMoney(fixedDiscount) }} HT</p>
      </template>
      <template #footer>
        <div class="w-full space-y-3 border-t border-gray-200 pt-3">
          <p v-if="quoting" class="text-sm text-indigo-700" role="status">Calcul en cours…</p>
          <div v-if="stepIncompleteOrigins.length" class="text-sm text-amber-800" role="status">
            <p>{{ step === 1 ? 'Choisissez un produit pour :' : 'Vérifiez les quantités pour :' }}</p>
            <button v-for="id in stepIncompleteOrigins" :key="id" type="button" :disabled="busy" class="mr-3 font-medium underline" @click="focusOrigin(id)">{{ original(id)?.reference || id }}</button>
          </div>
          <p v-else-if="!selections.length" class="text-sm text-gray-600" role="status">Cochez au moins un article à remplacer.</p>
          <p v-if="modalError || quoteError" class="text-sm text-red-700" role="alert">{{ quoteError || modalError }} <button v-if="quoteError && !quoting" type="button" class="underline" @click="refreshQuote">Réessayer</button></p>
          <p v-if="step === 2 && sellerCharge > 0" class="rounded border border-amber-300 bg-amber-50 p-2 text-sm font-semibold text-amber-900" role="status">À votre charge (max.) : {{ replacementMoney(sellerCharge) }} TTC. Aucun supplément client.</p>
          <label v-if="step === 2" class="flex cursor-pointer items-start gap-3 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-950">
            <input v-model="confirmed" :disabled="busy" type="checkbox" class="mt-0.5 h-4 w-4 shrink-0" />
            <span>Je confirme la disponibilité et les écarts de prix à ma charge.</span>
          </label>
          <div class="flex flex-wrap justify-end gap-2">
            <button type="button" :disabled="busy" class="border rounded px-3 py-2" @click="visible = false">Fermer</button>
            <button v-if="step === 2" type="button" :disabled="busy" class="border rounded px-3 py-2" @click="previousStep">Retour</button>
            <button v-if="step === 1" type="button" :disabled="!canPropose || busy || searching || !candidatesChosen" class="bg-indigo-600 text-white rounded px-3 py-2 disabled:opacity-50" @click="nextStep">Suivant</button>
            <button v-else type="button" :disabled="!canPropose || busy || quoting || !confirmed || !quoteReady" class="bg-indigo-600 text-white rounded px-3 py-2 disabled:opacity-50" @click="submit">{{ busy ? 'Traitement…' : 'Proposer' }}</button>
          </div>
        </div>
      </template>
    </ModalComp>
  </section>
</template>
<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import ModalComp from '@/components/ui/ModalComp.vue';
import { useAuthStore } from '@/stores/auth';
import ReplacementComparison from './ReplacementComparison.vue';
import EditReplacementCandidateModal from './EditReplacementCandidateModal.vue';
import ReplacementProductPicker from './ReplacementProductPicker.vue';
import ReplacementReferenceSearch from './ReplacementReferenceSearch.vue';
import type { OrderProduct } from '@/types';
import { cancelReplacement, fetchOrderReplacements, fetchReplacementProducts, previewReplacement, proposeReplacement,
  replacementLineProducts, replacementMoney, replacementStatus } from '@/api/remplacements';
import type { ReplacementAppliedProduct, ReplacementInput, ReplacementLine, ReplacementProduct, ReplacementProposal, ReplacementSelection } from '@/api/remplacements';

const props = defineProps<{ orderId: number; products: OrderProduct[]; canPropose: boolean }>();
const auth = useAuthStore();
const canManageLinks = computed(() => auth.isImpersonating);
const canEditRecommendations = computed(() => props.canPropose && canManageLinks.value);
const replacementStatusClasses: Record<ReplacementProposal['statut'], string> = {
  pending: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  accepted: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  rejected: 'bg-red-100 text-red-800 ring-red-200',
  expired: 'bg-amber-100 text-amber-900 ring-amber-200',
  cancelled: 'bg-gray-100 text-gray-700 ring-gray-200',
  confirmer: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};
const emit = defineEmits<{ changed: []; settled: [ids: number[]]; replacementConfirmation: [value: boolean] }>();
const proposals = ref<ReplacementProposal[]>([]);
const now = ref(Date.now());
const products = ref<ReplacementProduct[]>([]);
const candidatesByLine = ref<Record<number, number[]>>({});
const appliedSearch = ref('');
let searchVersion = 0;
let searchTimer: ReturnType<typeof window.setTimeout> | null = null;
let quoteTimer: ReturnType<typeof window.setTimeout> | null = null;
let quoteVersion = 0;
const searchTargetLineId = ref(0);
const selections = ref<ReplacementSelection[]>([]);
const selectedLineIds = ref<number[]>([]);
const visible = ref(false);
const step = ref<1 | 2>(1);
const stepsHeader = ref<HTMLOListElement | null>(null);
const search = ref('');
const searching = ref(false);
const busy = ref(false);
const quoting = ref(false);
const quoteError = ref('');
const confirmed = ref(false);
const preview = ref<ReplacementLine[] | null>(null);
const quoteHash = ref('');
const fixedDiscount = ref(0);
const modalError = ref('');
const loadError = ref('');
const loadInfo = ref('');
const expandedProposalId = ref<string | null>(null);
const candidateEditor = ref<InstanceType<typeof EditReplacementCandidateModal> | null>(null);
const sellerCharge = computed(() => (preview.value || []).reduce((sum, line) => sum + (line.charge_vendeur_max_ttc ?? line.charge_vendeur_ttc), 0));
const searchTarget = computed(() => selections.value.find(s => s.id_ligne_origine === searchTargetLineId.value));
const referenceSuggestions = computed(() => searchTarget.value ? availableProducts(searchTargetLineId.value) : []);
const original = (id: number) => props.products.find(p => p.id === id);
const product = (selection: ReplacementSelection) => products.value.find(p => p.id_produit === selection.id_produit_remplacement);
const quotedLines = computed(() => new Map((preview.value || []).map(line => [line.id_ligne_origine, line])));
const quotedLine = (id: number) => quotedLines.value.get(id);
const quotedProducts = computed(() => new Map((preview.value || []).flatMap(line =>
  replacementLineProducts(line).map(candidate => [`${line.id_ligne_origine}:${candidate.id_produit}`, candidate] as const))));
const quotedProduct = (selection: ReplacementSelection) => quotedProducts.value.get(`${selection.id_ligne_origine}:${selection.id_produit_remplacement}`);
const readySelections = computed(() => selections.value.filter(selectionReady));
const incompleteOrigins = computed(() => selectedLineIds.value.filter(id => originEnabled(id) && (!selectedFor(id).length || selectedFor(id).some(s => !selectionReady(s)))));
const missingCandidates = computed(() => selectedLineIds.value.filter(id => originEnabled(id) && !selectedFor(id).length));
const candidatesChosen = computed(() => selections.value.length > 0 && missingCandidates.value.length === 0 && selections.value.every(s => Boolean(product(s))));
const stepIncompleteOrigins = computed(() => step.value === 1 ? missingCandidates.value : incompleteOrigins.value);
const complete = computed(() => selections.value.length > 0 && readySelections.value.length === selections.value.length);
const quoteReady = computed(() => complete.value && Boolean(quoteHash.value) && preview.value?.length === new Set(selections.value.map(s => s.id_ligne_origine)).size);
const quoteKey = computed(() => JSON.stringify({ order: props.orderId, lines: selections.value.map(s => ({ ...s, price: product(s)?.prix_unitaire_ht, pack: product(s)?.pack, minimum: product(s)?.qte_mini, mode: product(s)?.vendu_par })) }));
const date = (value: string | number) => new Date(value).toLocaleString('fr-FR');
const input = (): ReplacementInput => ({ id_commande: props.orderId, lignes: selections.value, stock_confirme: confirmed.value, quote_hash: quoteHash.value });
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'Une erreur est survenue';
function responseDeadline(proposal: ReplacementProposal) {
  return new Date(proposal.date_expiration).getTime();
}
function remainingSeconds(proposal: ReplacementProposal) {
  return Math.max(0, Math.ceil((responseDeadline(proposal) - now.value) / 1000));
}
function countdown(proposal: ReplacementProposal) {
  const seconds = remainingSeconds(proposal);
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map(value => String(value).padStart(2, '0')).join(':');
}
function displayStatus(proposal: ReplacementProposal) {
  if (proposal.statut !== 'pending' || remainingSeconds(proposal) > 0) return proposal.statut;
  return proposal.lignes.some(line => line.statut === 'accepted' || line.accepted === 1) ? 'accepted' : 'expired';
}
function hasExpiredLines(proposal: ReplacementProposal) {
  return proposal.lignes.some(line => line.statut === 'expired') ||
    (proposal.statut === 'pending' && remainingSeconds(proposal) === 0);
}
function quantityErrors(selection: ReplacementSelection): string[] {
  const p = product(selection);
  if (!p) return ['Choisissez un produit de remplacement disponible.'];
  if (!Number.isSafeInteger(selection.quantite_pack) || selection.quantite_pack < 1 || selection.quantite_pack > 10000) return ['Quantité de paquets : saisissez un nombre entier entre 1 et 10 000.'];
  const q = selection.quantites;
  if (q.length !== 12) return ['Quantités incomplètes : sélectionnez à nouveau ce produit.'];
  const errors = q.flatMap((n, i) => {
    const label = p.tailles[i] || `Taille ${i + 1}`;
    if (!Number.isSafeInteger(n) || n < 0 || n > 10000) return [`${label} : saisissez un nombre entier entre 0 et 10 000.`];
    if (p.vendu_par === 'tailles') return n && !p.tailles[i] ? [`${label} : taille non disponible pour ce produit.`] : [];
    const maximum = p.pack[i] * selection.quantite_pack;
    return n <= maximum ? [] : [`${label} : ${n} pièce(s) saisie(s), maximum ${maximum} pour ${selection.quantite_pack} paquet(s).`];
  });
  if (errors.length) return errors;
  const count = q.reduce((sum, n) => sum + n, 0);
  if (count === 0) return ['Sélectionnez au moins une pièce pour ce produit.'];
  if (count > 10000) return ['Quantité totale : 10 000 pièces maximum par produit.'];
  if (p.vendu_par === 'tailles' && !(count >= p.qte_mini)) return [`Quantité totale : minimum ${p.qte_mini} pièce(s) pour ce produit (${count} saisie(s)).`];
  return [];
}
function selectionReady(selection: ReplacementSelection) { return quantityErrors(selection).length === 0; }
function stopQuote() {
  if (quoteTimer != null) window.clearTimeout(quoteTimer);
  quoteTimer = null;
  quoteVersion++;
  quoting.value = false;
}
function invalidate() {
  stopQuote();
  preview.value = null; quoteHash.value = ''; confirmed.value = false; fixedDiscount.value = 0; quoteError.value = '';
  if (visible.value && step.value === 2 && readySelections.value.length) {
    quoting.value = true;
    quoteTimer = window.setTimeout(() => { void refreshQuote(); }, 300);
  }
}
function availableProducts(lineId: number) {
  const ids = new Set(candidatesByLine.value[lineId] || []);
  return products.value.filter(p => ids.has(p.id_produit) && p.id_produit !== original(lineId)?.productId);
}
function selectedFor(lineId: number) { return selections.value.filter(s => s.id_ligne_origine === lineId && s.id_produit_remplacement > 0); }
async function focusStepHeader() {
  await nextTick();
  stepsHeader.value?.scrollIntoView({ block: 'start' });
  stepsHeader.value?.focus({ preventScroll: true });
}
function nextStep() {
  if (busy.value || searching.value || !candidatesChosen.value) return;
  step.value = 2;
  modalError.value = '';
  invalidate();
  void focusStepHeader();
}
function previousStep() {
  if (busy.value) return;
  step.value = 1;
  modalError.value = '';
  invalidate();
  void focusStepHeader();
}
async function focusOrigin(lineId: number) {
  if (step.value === 2 && !selectedFor(lineId).length) previousStep();
  searchTargetLineId.value = lineId;
  await nextTick();
  const section = document.getElementById(`replacement-origin-${lineId}`);
  section?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  section?.focus({ preventScroll: true });
}
function originEnabled(lineId: number) { return selections.value.some(s => s.id_ligne_origine === lineId); }
function toggleOrigin(lineId: number) {
  if (originEnabled(lineId)) selections.value = selections.value.filter(s => s.id_ligne_origine !== lineId);
  else selections.value.push(emptySelection(lineId));
  modalError.value = '';
  invalidate();
}
function emptySelection(lineId: number): ReplacementSelection { return { id_ligne_origine: lineId, id_produit_remplacement: 0, quantites: Array(12).fill(0), quantite_pack: 1 }; }
function removeProduct(selection: ReplacementSelection) {
  modalError.value = '';
  selections.value = selections.value.filter(s => s !== selection);
  if (!selectedFor(selection.id_ligne_origine).length) selections.value.push(emptySelection(selection.id_ligne_origine));
  invalidate();
}
function toggleProduct(lineId: number, productId: number, allowRemoval = true) {
  const current = selectedFor(lineId).find(s => s.id_produit_remplacement === productId);
  if (current) { if (allowRemoval) removeProduct(current); return; }
  if (selectedFor(lineId).length >= 10) { modalError.value = '10 articles maximum par ligne.'; return; }
  let selection = selections.value.find(s => s.id_ligne_origine === lineId && !s.id_produit_remplacement);
  if (!selection) {
    selections.value.push(emptySelection(lineId));
    selection = selections.value[selections.value.length - 1];
  }
  modalError.value = '';
  choose(selection, productId);
}
function originalAmount(id: number) {
  const p = original(id);
  const ht = p?.priceLineHT ?? p?.price;
  if (typeof ht !== 'number' || !Number.isFinite(ht)) return null;
  return `${replacementMoney(ht)} HT${p?.priceLineTTC != null ? ` · ${replacementMoney(p.priceLineTTC)} TTC` : ''}`;
}
function originalQuantity(id: number) {
  const p = original(id);
  const quantity = p?.quantites ? Object.values(p.quantites).reduce((sum, q) => sum + (Number(q) || 0), 0) : 0;
  return quantity || p?.quantite_total || p?.details || 0;
}
function originalSizes(id: number) {
  const p = original(id);
  return Object.entries(p?.quantites || {}).filter(([, quantity]) => Number(quantity) > 0).map(([key, quantity]) => {
    const label = p?.declinaisons?.[`d${key.slice(1)}_FR` as keyof NonNullable<OrderProduct['declinaisons']>];
    return `${label || 'Assortiment'} × ${quantity}`;
  }).join(' · ');
}
function sizeInputs(selection: ReplacementSelection) {
  const p = product(selection);
  return p ? p.tailles.map((label, index) => ({ label: label || 'Assortiment', index }))
    .filter(size => p.vendu_par === 'tailles' ? Boolean(p.tailles[size.index]) : p.pack[size.index] > 0) : [];
}
function updateSizeQuantity(selection: ReplacementSelection, index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.value === '' && !input.validity.badInput) input.value = '0';
  selection.quantites[index] = input.value === '' ? NaN : Number(input.value);
  invalidate();
}
function updatePacks(selection: ReplacementSelection) {
  const p = product(selection);
  if (p) selection.quantites = p.pack.map(q => q * selection.quantite_pack);
  invalidate();
}
function choose(selection: ReplacementSelection, productId: number) {
  if (selection.id_produit_remplacement === productId) return;
  selection.id_produit_remplacement = productId;
  selection.quantite_pack = 1;
  selection.quantites = Array(12).fill(0);
  const p = product(selection);
  if (p?.vendu_par !== 'tailles') updatePacks(selection);
  invalidate();
}
function chooseReference(productId: number) {
  if (!searchTarget.value) return;
  toggleProduct(searchTargetLineId.value, productId, false);
  search.value = products.value.find(p => p.id_produit === productId)?.reference || search.value;
}
function clearSearchTimer() {
  if (searchTimer != null) window.clearTimeout(searchTimer);
  searchTimer = null;
}
function protectedLineIds(rows: ReplacementProposal[]) {
  return rows.flatMap(proposal => proposal.lignes.filter(line => {
    const status = line.statut || (line.accepted ? 'accepted' : line.rejected ? 'rejected' : proposal.statut);
    return ['accepted', 'expired', 'pending'].includes(status);
  }).map(line => line.id_ligne_origine));
}
function onDetailsToggle(id: string, event: Event) {
  if (!(event.target as HTMLDetailsElement).open && expandedProposalId.value === id) expandedProposalId.value = null;
}
function openCandidateEditor(proposal: ReplacementProposal, line: ReplacementLine, product: ReplacementAppliedProduct, action: 'quantity' | 'remove' | 'product') {
  if (!canEditRecommendations.value) return;
  void candidateEditor.value?.open(proposal, line, product, action);
}
async function onCandidateSaved() {
  loadInfo.value = 'Recommandation mise à jour.';
  await load(); emit('changed');
}
async function load() {
  const id = props.orderId;
  try {
    const result = await fetchOrderReplacements(id, canManageLinks.value);
    if (id !== props.orderId) return;
    const changed = proposals.value.length > 0 && (proposals.value.length !== result.length || proposals.value.some(old => {
      const current = result.find(proposal => proposal.id_jet === old.id_jet);
      return !current || current.statut !== old.statut || current.confirmation_remplacements !== old.confirmation_remplacements ||
        JSON.stringify(current.lignes) !== JSON.stringify(old.lignes);
    }));
    proposals.value = result;
    loadError.value = '';
    emit('settled', protectedLineIds(result));
    emit('replacementConfirmation', result.some(proposal => proposal.confirmation_remplacements === true));
    if (changed) emit('changed');
  } catch (error) { loadError.value = errorMessage(error); emit('replacementConfirmation', false); }
}
async function searchProducts() {
  clearSearchTimer();
  const version = ++searchVersion;
  const orderId = props.orderId;
  const term = search.value.trim();
  searching.value = true;
  modalError.value = '';
  try {
    const requests = new Map<string, ReturnType<typeof fetchReplacementProducts>>();
    const results = await Promise.all(selectedLineIds.value.map(async lineId => {
      const category = original(lineId)?.id_categorie;
      const key = term ? 'reference' : category ? `category:${category}` : `line:${lineId}`;
      let request = requests.get(key);
      if (!request) {
        request = fetchReplacementProducts(orderId, term, lineId, canManageLinks.value);
        requests.set(key, request);
      }
      return { lineId, rows: await request };
    }));
    if (version !== searchVersion || orderId !== props.orderId || !visible.value) return;
    const selectedIds = new Set(selections.value.map(s => s.id_produit_remplacement));
    const merged = new Map(products.value.filter(p => selectedIds.has(p.id_produit)).map(p => [p.id_produit, p]));
    const byLine: Record<number, number[]> = {};
    for (const result of results) {
      byLine[result.lineId] = result.rows.map(p => p.id_produit);
      result.rows.forEach(p => merged.set(p.id_produit, p));
    }
    products.value = [...merged.values()];
    candidatesByLine.value = byLine;
    appliedSearch.value = term;
  } catch (error) { if (version === searchVersion) modalError.value = errorMessage(error); }
  finally { if (version === searchVersion) searching.value = false; }
}
async function open() {
  if (!props.canPropose) return;
  loadInfo.value = '';
  modalError.value = '';
  await load();
  if (!props.canPropose) return;
  const alreadyReplaced = new Set(protectedLineIds(proposals.value));
  const eligible = props.products.map(p => p.id).filter(id => !alreadyReplaced.has(id));
  if (!eligible.length) { loadError.value = 'Tous les articles sont déjà proposés, remplacés ou hors délai. Ajoutez une nouvelle ligne en rupture pour proposer un autre remplacement.'; return; }
  selectedLineIds.value = [...new Set(eligible)];
  selections.value = selectedLineIds.value.map(emptySelection);
  searchTargetLineId.value = eligible[0];
  candidatesByLine.value = {};
  search.value = '';
  appliedSearch.value = '';
  preview.value = null;
  confirmed.value = false;
  step.value = 1;
  visible.value = true;
  await searchProducts();
}
async function refreshQuote() {
  stopQuote();
  if (!props.canPropose || !visible.value || step.value !== 2 || !readySelections.value.length) return;
  const version = quoteVersion;
  const orderId = props.orderId;
  const lines = readySelections.value.map(s => ({ ...s, quantites: [...s.quantites] }));
  quoting.value = true;
  quoteError.value = '';
  try {
    const result = await previewReplacement({ id_commande: orderId, lignes: lines, stock_confirme: false }, canManageLinks.value);
    if (version !== quoteVersion || orderId !== props.orderId || !visible.value) return;
    preview.value = result.lignes;
    quoteHash.value = result.quote_hash;
    fixedDiscount.value = result.remise_fixe_commande_ht || 0;
  }
  catch (error) { if (version === quoteVersion && visible.value) quoteError.value = errorMessage(error); }
  finally { if (version === quoteVersion) quoting.value = false; }
}
async function submit() {
  if (!props.canPropose || step.value !== 2 || busy.value || quoting.value || !confirmed.value || !quoteReady.value) return;
  const orderId = props.orderId;
  busy.value = true;
  modalError.value = '';
  try {
    const result = await proposeReplacement(input());
    if (orderId !== props.orderId) return;
    loadInfo.value = result.lien_conserve ? 'Articles ajoutés à la proposition.' : '';
    visible.value = false;
    await load();
    emit('changed');
  } catch (error) { modalError.value = errorMessage(error); invalidate(); }
  finally { busy.value = false; }
}
async function cancel(id: string) {
  if (busy.value) return;
  busy.value = true;
  try { await cancelReplacement(id, props.orderId); loadInfo.value = ''; await load(); emit('changed'); }
  catch (error) { loadError.value = errorMessage(error); }
  finally { busy.value = false; }
}
watch(search, () => {
  clearSearchTimer();
  searchVersion++;
  candidatesByLine.value = {};
  if (!visible.value) return;
  searching.value = true;
  searchTimer = window.setTimeout(() => { void searchProducts(); }, search.value.trim() ? 300 : 0);
}, { flush: 'sync' });
watch(visible, value => {
  if (!value) { clearSearchTimer(); searchVersion++; searching.value = false; stopQuote(); }
});
watch(() => props.canPropose, allowed => { if (!allowed) visible.value = false; });
watch(() => props.orderId, () => { searchVersion++; searching.value = false; candidatesByLine.value = {}; visible.value = false; void load(); }, { immediate: true });
watch(quoteKey, invalidate, { flush: 'sync' });
watch(() => proposals.value.filter(proposal => proposal.statut === 'pending' && remainingSeconds(proposal) === 0).map(proposal => proposal.id_jet).join(','), ids => {
  if (ids && !busy.value) void load();
});
const clock = window.setInterval(() => { now.value = Date.now(); }, 1000);
const timer = window.setInterval(() => { if (!visible.value && !busy.value) void load(); }, 30000);
onUnmounted(() => { window.clearInterval(clock); window.clearInterval(timer); clearSearchTimer(); searchVersion++; stopQuote(); });
defineExpose({ open });
</script>
